/**
 * ChooseAiBuddy — the AI companions roster. A member can open a buddy's
 * profile or make one their AI Buddy; switching asks for confirmation because
 * only one buddy can be followed at a time (mobile drawer/aiBuddy parity).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useAiBuddies from '@wlb/shared/hooks/useAiBuddies';
import useChooseAiBuddy from '@wlb/shared/hooks/useChooseAiBuddy';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { AIBuddy } from '@wlb/shared/types/user';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { InfiniteScrollFooter } from '../components/ui/InfiniteScrollFooter';
import { ScrollToTopButton } from '../components/ui/ScrollToTopButton';
import { MorePage } from '../features/more/MorePage';
import { MoreLoading } from '../features/more/MoreStatus';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { useOpenUserProfile } from '../features/profile/useOpenUserProfile';
import '../features/more/more.css';

export function ChooseAiBuddy() {
  const { t } = useTranslation();
  const openUserProfile = useOpenUserProfile();
  const { buddies, loading, loadingMore, hasMore, error, loadMoreError, loaded, refresh, loadMore } =
    useAiBuddies();
  const { isCurrent, currentAgentId, pendingBuddyId, choose } = useChooseAiBuddy();
  const [target, setTarget] = useState<AIBuddy | null>(null);

  // The buddy the member already follows leads the list.
  const orderedBuddies = useMemo(
    () => [...buddies].sort((a, b) => Number(isCurrent(b)) - Number(isCurrent(a))),
    [buddies, isCurrent],
  );

  const confirmChoice = async () => {
    if (!target) return;
    const result = await choose(target);
    setTarget(null);
    if (!result.success) {
      ToastService.show({
        type: 'error',
        message: result.message || t('common.errors.generic'),
      });
    }
  };

  const renderBody = () => {
    if (loading && buddies.length === 0) return <MoreLoading />;

    if (error && buddies.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={error || t('aiBuddy.list.loadError')}
          onAction={refresh}
          busy={loading}
        />
      );
    }

    if (loaded && buddies.length === 0) {
      return (
        <ListStatus
          variant="empty"
          title={t('aiBuddy.list.empty')}
          onAction={refresh}
          busy={loading}
        />
      );
    }

    return (
      <>
      <ul className="ai-buddy-list" aria-label={t('aiBuddy.accessibility.list')}>
        {orderedBuddies.map(buddy => {
          const selected = isCurrent(buddy);
          return (
            <li
              key={buddy.id}
              className={`ai-buddy-card${selected ? ' ai-buddy-card--current' : ''}`}
            >
              <div className="ai-buddy-card__header">
                <Avatar src={buddy.image} name={buddy.name} size="lg" />
                <div className="ai-buddy-card__identity">
                  <p className="ai-buddy-card__name">{buddy.name}</p>
                  <p className="ai-buddy-card__role">{buddy.role}</p>
                </div>
              </div>

              {selected && (
                <span className="ai-buddy-card__badge">{t('aiBuddy.list.current')}</span>
              )}

              {buddy.description && (
                <p className="ai-buddy-card__description">{buddy.description}</p>
              )}

              <div className="ai-buddy-card__actions">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openUserProfile(buddy.profileUserGuid || buddy.agentGuid)}
                >
                  {t('aiBuddy.list.explore')}
                </Button>
                <Button
                  size="sm"
                  disabled={pendingBuddyId === buddy.id}
                  onClick={() => setTarget(buddy)}
                >
                  {t(selected ? 'profile.buttons.unfollow' : 'aiBuddy.details.chooseButton')}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
      {loadMoreError ? (
        <ListStatus
          variant="error"
          layout="inline"
          message={loadMoreError}
          onAction={loadMore}
          busy={loadingMore}
        />
      ) : (
        <InfiniteScrollFooter
          hasNextPage={hasMore}
          isFetchingMore={loadingMore}
          onLoadMore={loadMore}
          itemCount={buddies.length}
        />
      )}
      </>
    );
  };

  const renderPrompt = () => {
    if (!target) return null;

    const unfollowing = isCurrent(target);
    // Only one AI Buddy is allowed at a time, so replacing one needs its own copy.
    const switching = !unfollowing && currentAgentId != null;

    const prompt = unfollowing
      ? {
          title: t('profile.unfollow.title', { name: target.name }),
          message: t('profile.unfollow.description', { name: target.name }),
          confirmLabel: t('profile.unfollow.confirm'),
          cancelLabel: t('common.buttons.cancel'),
        }
      : switching
        ? {
            title: t('aiBuddy.changeBuddy.title'),
            message: t('aiBuddy.changeBuddy.description'),
            confirmLabel: t('aiBuddy.changeBuddy.confirm'),
            cancelLabel: t('aiBuddy.changeBuddy.cancel'),
          }
        : {
            title: t('aiBuddy.modal.followBuddyConfirmTitle'),
            message: t('aiBuddy.modal.followBuddyConfirmDesc'),
            confirmLabel: t('aiBuddy.details.chooseButton'),
            cancelLabel: t('common.buttons.cancel'),
          };

    return (
      <ConfirmModal
        title={prompt.title}
        message={prompt.message}
        confirmLabel={prompt.confirmLabel}
        cancelLabel={prompt.cancelLabel}
        mediaUrl={target.image || null}
        loading={pendingBuddyId === target.id}
        onClose={() => setTarget(null)}
        onConfirm={() => { confirmChoice().catch(() => undefined); }}
      />
    );
  };

  return (
    <MorePage title={t('aiBuddy.list.title')} subtitle={t('aiBuddy.list.subtitle')}>
      {renderBody()}
      {renderPrompt()}
      <ScrollToTopButton enabled={buddies.length > 0} />
    </MorePage>
  );
}
